/**
 * TourProvider — Guided in-app tour engine (#1559)
 *
 * Wraps the app with a react-joyride v3 instance and exposes start/dismiss
 * via TourContext. State machine:
 *
 *   idle → running → done
 *           ↑
 *        (replay)
 *
 * Persistence: on first completion or dismissal we PATCH /auth/tour/complete
 * to set users.tour_completed_at. Replay does NOT re-PATCH (the user has
 * already opted in once); the column stays set.
 *
 * Cross-route navigation: each step declares its target route. When joyride
 * advances to a step whose route differs from the current location, we
 * navigate first and resume on the next tick after the route mounts.
 *
 * Controlled mode: we drive `stepIndex` from React state and respond to
 * joyride's `onEvent` callback for STEP_AFTER / STATUS changes.
 */

import { useCallback, useMemo, useState, useEffect, useRef } from 'react';
import type { ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Joyride, ACTIONS, EVENTS, STATUS } from 'react-joyride';
import type { EventData, Step, Styles, PartialDeep } from 'react-joyride';
import { authApi } from '../../lib/api/auth';
import { useAuth } from '../../hooks/useAuth';
import { useClubs } from '../../hooks/useClubs';
import { TOUR_STEPS, resolveStepRoute } from './tourSteps';
import { TourTooltip } from './TourTooltip';
import { TourContext, type TourContextValue } from './useTour';

/**
 * Layer 2 token bridge for joyride styles.
 * Only overlay + arrow remain here — the tooltip body is rendered by
 * TourTooltip (custom tooltipComponent) so it can run enter animations.
 */
function getJoyrideStyles(): PartialDeep<Styles> {
  return {
    overlay: {
      backgroundColor: 'rgba(0, 0, 0, 0.6)',
    },
    arrow: {
      color: 'var(--oro-surface)',
    },
  };
}

interface TourProviderProps {
  children: ReactNode;
}

export function TourProvider({ children }: TourProviderProps) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { data: clubs } = useClubs();

  const [run, setRun] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  // True while we have paused joyride to navigate between routes mid-tour.
  // The TourPrompt reads isActive to decide whether to render — without this
  // flag, every cross-route step would re-show the prompt during the 250ms gap.
  const [transitioning, setTransitioning] = useState(false);
  const persistedRef = useRef(false);

  const hasCompleted = !!user?.tour_completed_at;

  // Resolve dynamic :slug placeholders against the user's first club.
  // If the user has zero clubs, club-scoped steps are filtered out.
  const firstClubSlug = clubs && clubs.length > 0 ? clubs[0].slug : null;
  // Multi-club gate (#2032): the ContextSheet "Clubs" section only renders for
  // users in >1 club, so its tour step must be filtered out otherwise (else
  // Joyride hits TARGET_NOT_FOUND and cascades).
  const hasMultipleClubs = !!clubs && clubs.length > 1;

  const steps: Step[] = useMemo(() => {
    const out: Step[] = [];
    for (const s of TOUR_STEPS) {
      // Skip steps whose anchor lives in a club-aware component (#1601)
      if (s.requiresClub && !firstClubSlug) continue;
      // Skip multi-club-only anchors for single-club / clubless users (#2032)
      if (s.requiresMultipleClubs && !hasMultipleClubs) continue;
      const resolvedRoute = resolveStepRoute(s.route, firstClubSlug);
      if (!resolvedRoute) continue; // Skip club-scoped steps for clubless users
      out.push({
        target: `[data-tour="${s.target}"]`,
        title: s.title,
        content: s.content,
        placement: s.placement ?? 'bottom',
        skipBeacon: true,
        // Stash route + target name on the step for the event handler
        data: { route: resolvedRoute, target: s.target },
      });
    }
    return out;
  }, [firstClubSlug, hasMultipleClubs]);

  const persistCompletion = useCallback(async () => {
    if (persistedRef.current) return;
    persistedRef.current = true;
    // Keep isActive=true while the PATCH + ['me'] refetch are in flight so
    // TourPromptGate does not briefly re-render the "Want a quick tour?"
    // modal between tour-end and the user payload landing with a non-null
    // tour_completed_at.
    setTransitioning(true);
    try {
      await authApi.completeTour();
      await queryClient.invalidateQueries({ queryKey: ['me'] });
    } catch (err) {
      console.error('Failed to persist tour completion:', err);
      persistedRef.current = false; // Allow retry
    } finally {
      setTransitioning(false);
    }
  }, [queryClient]);

  const start = useCallback(() => {
    persistedRef.current = hasCompleted; // Replay: skip persistence
    setStepIndex(0);

    // If the first step's route doesn't match the current location, navigate
    // first and defer activation. Otherwise the mismatch effect would fire on
    // the very next render and end the tour before it starts. This is the
    // critical case for Settings → Replay (user is on /settings) and for
    // first-run prompts shown on any route other than the first step's route.
    const firstStepRoute = (steps[0]?.data as { route: string } | undefined)?.route;
    if (firstStepRoute && firstStepRoute !== location.pathname) {
      setTransitioning(true);
      navigate(firstStepRoute);
      // Wait for route mount so joyride resolves the target on the new page
      setTimeout(() => {
        setRun(true);
        setTransitioning(false);
      }, 250);
    } else {
      setRun(true);
    }
  }, [hasCompleted, steps, location.pathname, navigate]);

  const dismiss = useCallback(async () => {
    await persistCompletion();
  }, [persistCompletion]);

  const handleEvent = useCallback((data: EventData) => {
    const { action, index, status, type, step } = data;

    // Tour ended: completed all steps OR user pressed skip/close
    if (status === STATUS.FINISHED || status === STATUS.SKIPPED) {
      setRun(false);
      setStepIndex(0);
      void persistCompletion();
      return;
    }

    // Step changed — handle cross-route navigation
    if (type === EVENTS.STEP_AFTER || type === EVENTS.TARGET_NOT_FOUND) {
      const nextIndex = index + (action === ACTIONS.PREV ? -1 : 1);

      if (nextIndex < 0 || nextIndex >= steps.length) {
        setRun(false);
        setStepIndex(0);
        void persistCompletion();
        return;
      }

      const nextRoute = (steps[nextIndex]?.data as { route: string } | undefined)?.route;
      const currentRoute = (step.data as { route: string } | undefined)?.route ?? location.pathname;

      // If the next step lives on a different route, navigate before advancing.
      // Joyride re-resolves the target after the route change.
      if (nextRoute && nextRoute !== currentRoute && nextRoute !== location.pathname) {
        setRun(false); // Pause to avoid mid-navigation flicker
        setTransitioning(true);
        navigate(nextRoute);
        // Resume on next tick after route mounts
        setTimeout(() => {
          setStepIndex(nextIndex);
          setRun(true);
          setTransitioning(false);
        }, 250);
      } else {
        setStepIndex(nextIndex);
      }
    }
  }, [steps, location.pathname, navigate, persistCompletion]);

  // If the user navigates via browser back/forward mid-tour and lands on a
  // route that doesn't match the current step, end gracefully.
  useEffect(() => {
    if (!run) return;
    const currentStepRoute = (steps[stepIndex]?.data as { route: string } | undefined)?.route;
    if (currentStepRoute && currentStepRoute !== location.pathname) {
      setRun(false);
      void persistCompletion();
    }
  }, [location.pathname, run, stepIndex, steps, persistCompletion]);

  const currentStepTarget = run
    ? (steps[stepIndex]?.data as { target?: string } | undefined)?.target ?? null
    : null;

  const value: TourContextValue = {
    isActive: run || transitioning,
    hasCompleted,
    start,
    dismiss,
    currentStepTarget,
  };

  return (
    <TourContext.Provider value={value}>
      {children}
      {steps.length > 0 && (
        <Joyride
          steps={steps}
          run={run}
          stepIndex={stepIndex}
          continuous
          onEvent={handleEvent}
          styles={getJoyrideStyles()}
          tooltipComponent={TourTooltip}
          options={{
            showProgress: true,
            buttons: ['back', 'skip', 'close', 'primary'],
            zIndex: 60, // Above bottom nav (z-50) but below toasts
          }}
          locale={{
            back: 'Back',
            close: 'Close',
            last: 'Done',
            next: 'Next',
            skip: 'Skip',
          }}
        />
      )}
    </TourContext.Provider>
  );
}
